import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { site, absoluteUrl } from '../seo'

/**
 * Per-page metadata: title, description, canonical, Open Graph and Twitter
 * cards, plus any JSON-LD passed in.
 *
 * Note these tags are written by React on the client. Google renders JS so it
 * picks them up, but social crawlers (WhatsApp, LinkedIn, Facebook) do not —
 * which is why scripts/generate-seo.js also bakes the same values into static
 * HTML per route at build time. Keep the two in step: the marketing routes get
 * their copy from src/seo/site.json, which both sides read.
 */
function Seo({
  title,
  description,
  image,
  noindex = false,
  type = 'website',
  schema = null,
  canonicalPath
}) {
  const location = useLocation()
  const path = canonicalPath || location.pathname
  const url = absoluteUrl(path)

  const fullTitle = title || site.routes['/'].title
  const metaDescription = description || site.routes['/'].description
  const socialImage = absoluteUrl(image || site.socialImage)

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : []

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={url} />

      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={site.siteName} />
      <meta property="og:locale" content={site.locale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={socialImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={socialImage} />

      {schemas.map((entry, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  )
}

export default Seo
